// Sidebar — the Console Glass navigation panel (task 355.4).
//
// Floating dark-slate panel in both palettes: wordmark, tenant pill, grouped AntD Menu (theme="dark",
// styled by the NAV_MENU tokens in glassTheme.ts) and the user card. Destinations and permission gates
// come from navModel.ts — the same model the command palette uses.
//
// History: F11 (118) document type tree, F28 (135) My Tasks badge, 155/156 GL sub-menu,
// 155 refactor: tree folded into the Menu. 355.4: groups (Operate / Ledger / Configure / Integration;
// 381 folded Integration into Operate).
// 359: chart-of-accounts sections under General ledger › Accounts (selection follows ?coa=).
// 381: the Payments shortcut and payment documents highlight Payments and leave DATA HUB closed.
// 401.11: system-intake document types carry a small neutral inbox mark (documents arrive, nobody creates them).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Menu, Skeleton } from 'antd';
import { Inbox } from 'lucide-react';
import type { MenuProps } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSidebarStore } from '../../../stores/sidebarStore';
import logoUrl from '../../../assets/Bancorix.svg';
import markUrl from '../../../assets/Bancorix-mark.svg';
import TenantSwitcher from '../../../features/auth/components/TenantSwitcher';
import UserMenu from './UserMenu';
import { useNavGroups } from './useNavGroups';
import {
  DATA_HUB_KEY,
  DOC_CAT_PREFIX,
  deriveOpenKeys,
  deriveSelectedKey,
  type NavNode,
} from './navModel';
import styles from './Shell.module.css';

type MenuItem = Required<MenuProps>['items'][number];

const LOADING_KEY = '__doc_loading__';

const SYSTEM_INTAKE_HINT =
  'System intake — documents arrive from the App, integrations or workflows; not created by hand';

function itemLabel(node: NavNode): React.ReactNode {
  if (!node.count && !node.systemIntake) return node.label;
  return (
    <span className={styles.itemLabel}>
      <span className={styles.itemText}>{node.label}</span>
      {node.systemIntake && (
        <span className={styles.intake} title={SYSTEM_INTAKE_HINT} aria-label="System intake">
          <Inbox size={12} strokeWidth={1.8} aria-hidden="true" />
        </span>
      )}
      {!!node.count && (
        <span
          className={[styles.count, node.key === '/assignments' ? styles.countAttention : ''].join(
            ' ',
          )}
        >
          {node.count > 999 ? '999+' : node.count}
        </span>
      )}
    </span>
  );
}

function toMenuItem(node: NavNode, documentTreeLoading: boolean): MenuItem {
  const Icon = node.icon;
  const icon = Icon ? <Icon size={17} strokeWidth={1.7} aria-hidden="true" /> : undefined;

  if (node.key === DATA_HUB_KEY && documentTreeLoading) {
    return {
      key: node.key,
      icon,
      label: node.label,
      children: [
        {
          key: LOADING_KEY,
          label: <Skeleton active title={false} paragraph={{ rows: 3, width: '80%' }} />,
          disabled: true,
        },
      ],
    };
  }

  if (node.children) {
    return {
      key: node.key,
      icon,
      label: node.label,
      children: node.children.map((child) => toMenuItem(child, documentTreeLoading)),
    };
  }

  return { key: node.key, icon, label: itemLabel(node) };
}

function Sidebar(): React.ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const { collapsed } = useSidebarStore();
  const { groups, documentTreeLoading, payments } = useNavGroups();
  const { pathname } = location;

  const selectedKey = useMemo(() => deriveSelectedKey(pathname, payments), [pathname, payments]);

  // Controlled open keys — start from the route (GL open by default, as before), user can toggle.
  const [openKeys, setOpenKeys] = useState<string[]>(() => {
    const base = deriveOpenKeys(pathname);
    if (!base.includes('/gl')) base.push('/gl');
    return base;
  });

  // Keep the section of the current route open when navigating.
  useEffect(() => {
    const needed = deriveOpenKeys(pathname, payments);
    setOpenKeys((prev) => {
      const missing = needed.filter((k) => !prev.includes(k));
      return missing.length ? [...prev, ...missing] : prev;
    });
  }, [pathname, payments]);

  // Open the document category that holds the current document type once the tree has loaded.
  useEffect(() => {
    if (!pathname.startsWith('/documents/')) return;
    const typeCode = pathname.split('/')[2];
    // A payment document is shown under the Payments shortcut, outside DATA HUB.
    if (typeCode === 'category' || payments?.typeCodes.includes(typeCode)) return;
    const documents = groups.flatMap((g) => g.items).find((n) => n.key === DATA_HUB_KEY)?.children;
    const category = documents?.find((cat) =>
      cat.children?.some((dt) => dt.key === `/documents/${typeCode}`),
    );
    if (category) {
      setOpenKeys((prev) => (prev.includes(category.key) ? prev : [...prev, category.key]));
    }
  }, [groups, pathname, payments]);

  const items = useMemo(
    (): MenuItem[] =>
      groups
        .filter((g) => g.items.length > 0)
        .map((g) => ({
          type: 'group' as const,
          key: g.key,
          label: g.label,
          children: g.items.map((node) => toMenuItem(node, documentTreeLoading)),
        })),
    [groups, documentTreeLoading],
  );

  const handleSelect: MenuProps['onSelect'] = useCallback(
    ({ key }: { key: string }) => {
      // Skip non-navigable keys (loading placeholder, category openers).
      if (key.startsWith('__') || key.startsWith(DOC_CAT_PREFIX)) return;
      navigate(key);
    },
    [navigate],
  );

  return (
    <nav
      className={[styles.nav, collapsed ? styles.navCollapsed : ''].join(' ')}
      aria-label="Main navigation"
    >
      <button
        type="button"
        className={styles.logo}
        onClick={() => navigate('/dashboard')}
        aria-label="Bancorix — go to dashboard"
      >
        {collapsed ? (
          <img src={markUrl} alt="Bancorix" className={styles.logoMarkImg} />
        ) : (
          <img src={logoUrl} alt="Bancorix" />
        )}
      </button>

      <TenantSwitcher />

      <div className={styles.menuScroll}>
        <Menu
          mode="inline"
          theme="dark"
          inlineIndent={14}
          inlineCollapsed={collapsed}
          selectedKeys={[selectedKey]}
          openKeys={collapsed ? [] : openKeys}
          onOpenChange={setOpenKeys}
          onSelect={handleSelect}
          items={items}
        />
      </div>

      <UserMenu collapsed={collapsed} />
    </nav>
  );
}

export default Sidebar;
